import axios, { AxiosError } from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api/v1';

// Custom params serializer for FastAPI compatibility
// FastAPI expects: indicators=MA&indicators=RSI (not indicators[]=MA&indicators[]=RSI)
function serializeParams(params: Record<string, unknown>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(v)}`));
    } else {
      parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`);
    }
  }
  return parts.join('&');
}

export const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 120000, // 120s for LLM analysis requests
  headers: {
    'Content-Type': 'application/json',
  },
  paramsSerializer: serializeParams,
});

// Request interceptor — auto-inject Bearer token if logged in.
// Token is stored in localStorage 'auth_token' by useAuth().login().
api.interceptors.request.use((config) => {
  if (typeof window !== 'undefined') {
    const token = window.localStorage.getItem('auth_token');
    if (token) {
      config.headers = config.headers ?? {};
      // axios v1: headers is AxiosHeaders; setItem-compatible API
      config.headers.Authorization = `Bearer ${token}`;
    }
  }
  return config;
});

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError) => {
    // Auto-clear expired token so UI can show login modal next time
    if (error.response?.status === 401 && typeof window !== 'undefined') {
      const reason = (error.response.data as { detail?: string })?.detail ?? '';
      if (/expired|validate credentials/i.test(reason)) {
        window.localStorage.removeItem('auth_token');
      }
    }
    return Promise.reject(error);
  }
);

// API helper functions
export const apiClient = {
  // Quotes
  getQuote: (symbol: string, realtime?: boolean) => api.get(`/quotes/${symbol}`, { params: realtime ? { realtime } : undefined }),
  getQuoteHistory: (symbol: string, params?: { start_date?: string; end_date?: string; period?: string }) =>
    api.get(`/quotes/${symbol}/history`, { params }),
  getQuoteRealtime: (symbol: string) => api.get(`/quotes/realtime/${symbol}`),
  searchStocks: (query: string) => api.get('/quotes/search', { params: { q: query } }),
  searchUniversal: (query: string) => api.get('/search/universal', { params: { q: query } }),
  getMarketOverview: () => api.get('/quotes/market/overview'),
  refreshStockData: (symbol: string) => api.post(`/quotes/${symbol}/refresh`),

  // Real-time quotes (new dedicated endpoints)
  async getRealtimeQuote(symbol: string, forceRefresh: boolean = false, forceLatest: boolean = false) {
    return api.get(`/realtime/quote/${symbol}`, {
      params: { 
        force_refresh: forceRefresh,
        force_latest: forceLatest
      }
    });
  },
  getBatchRealtimeQuotes: (symbols: string[]) => 
    api.post('/realtime/quotes/batch', symbols),
  getRealtimeHistory: (symbol: string, params?: { period?: string; include_current?: boolean }) =>
    api.get(`/realtime/history/${symbol}`, { params }),
  getTradingSession: () => api.get('/realtime/trading-session'),
  getMarketStatus: () => api.get('/realtime/market-status'),

  // Analysis
  getIndicators: (symbol: string, params?: { indicators?: string[]; period?: string }) =>
    api.get(`/analysis/${symbol}/indicators`, { params }),
  getTrendAnalysis: (symbol: string) => api.get(`/analysis/${symbol}/trend`),
  getPatternRecognition: (symbol: string) => api.get(`/analysis/${symbol}/patterns`),
  getSupportResistance: (symbol: string) => api.get(`/analysis/${symbol}/support-resistance`),
  getLLMTechnicalAnalysis: (symbol: string, params?: { name?: string; provider?: string }) =>
    api.get(`/analysis/${symbol}/llm-analysis`, { params }),
  getLLMProviders: () => api.get('/analysis/llm-providers'),

  // Stock Insight (aggregated profile)
  getStockInsight: (symbol: string) => api.get(`/stock-insight/${symbol}`),
  getStockInsightDeep: (symbol: string) => api.get(`/stock-insight/${symbol}/deep`),

  // Backtest
  runBacktest: (data: BacktestRequest) => api.post('/backtest/run', data),
  getBacktestResult: (backtestId: string) => api.get(`/backtest/${backtestId}`),
  getBacktestHistory: () => api.get('/backtest/history'),
  getStrategies: () => api.get('/backtest/strategies'),

  // Alerts
  getAlerts: () => api.get('/alerts'),
  createAlert: (data: CreateAlertRequest) => api.post('/alerts', data),
  updateAlert: (alertId: number, data: UpdateAlertRequest) => api.patch(`/alerts/${alertId}`, data),
  deleteAlert: (alertId: number) => api.delete(`/alerts/${alertId}`),
  getAlertHistory: () => api.get('/alerts/history'),

  // Sync
  syncToday: (symbol: string) => api.post(`/quotes/${symbol}/sync-today`),
};

// Types for API requests
export interface BacktestRequest {
  symbol: string;
  strategy: string;
  start_date: string;
  end_date: string;
  initial_capital: number;
  params?: Record<string, unknown>;
}

export type AlertConditionType = 
  | 'price_above'
  | 'price_below'
  | 'price_change_pct'
  | 'volume_spike'
  | 'indicator_cross'
  | 'indicator_threshold'
  | 'pattern_detected';

export interface AlertCondition {
  type: AlertConditionType;
  value: number | string;
  params?: Record<string, unknown>;
}

export interface CreateAlertRequest {
  symbol: string;
  name: string;
  condition: AlertCondition;
  notification_channels?: string[];
  cooldown_minutes?: number;
  expires_at?: string;
  note?: string;
}

export interface UpdateAlertRequest {
  name?: string;
  condition?: AlertCondition;
  notification_channels?: string[];
  cooldown_minutes?: number;
  is_active?: boolean;
  expires_at?: string;
  note?: string;
}

export default api;
